import { watch, onScopeDispose } from "vue";

/**
 * useBodyScrollLock — lock centralizado del scroll del <body> para modales/drawers.
 *
 * Reemplaza el `document.body.style.overflow = 'hidden'` disperso por componente.
 * - Ref-count: soporta modales apilados → sólo desbloquea cuando cierra el ÚLTIMO.
 * - Compensa el ancho de la scrollbar (padding-right) → sin salto de layout al ocultar
 *   la barra de la página en barras clásicas (Win/Linux). En overlay (Mac) el ancho es 0.
 * - Auto-cleanup: si el componente se desmonta con el lock activo, lo libera.
 *
 * Uso:  useBodyScrollLock(() => props.modalStatus)   // getter o ref booleano
 */

let lockCount = 0;
let prevOverflow = "";
let prevPaddingRight = "";

function lock() {
  if (lockCount++ > 0) return; // ya bloqueado por otro modal
  const body = document.body;
  const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
  prevOverflow = body.style.overflow;
  prevPaddingRight = body.style.paddingRight;
  body.style.overflow = "hidden";
  if (scrollbarWidth > 0) {
    const current = parseFloat(getComputedStyle(body).paddingRight) || 0;
    body.style.paddingRight = `${current + scrollbarWidth}px`;
  }
}

function unlock() {
  if (lockCount === 0) return;
  if (--lockCount > 0) return; // aún hay otro modal abierto
  const body = document.body;
  body.style.overflow = prevOverflow;
  body.style.paddingRight = prevPaddingRight;
}

export function useBodyScrollLock(source) {
  let locked = false;
  const set = (open) => {
    if (open && !locked) {
      lock();
      locked = true;
    } else if (!open && locked) {
      unlock();
      locked = false;
    }
  };
  const stop = watch(source, set, { immediate: true });
  onScopeDispose(() => {
    stop();
    if (locked) {
      unlock();
      locked = false;
    }
  });
}
